import { type Token, TokenKind } from '../../json/tokens';
import { hashCommentSpans, isInsideOpaqueSpan } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Delete comments. JSON has none; JSONC, `tsconfig.json` and every hand-written config do.
 *
 * Three forms are removed: `//` to end of line, `/* … *\/` (including one left unterminated
 * at EOF), and `#` to end of line, which `jsonc-parser` does not know about and which is
 * reconstructed from its `Unknown` token here.
 *
 * The counter-example that motivates the whole design is `{"a":"// not a comment"}`. The
 * legacy rule was `/\/\/.*$/gm` over raw text, so every URL in a value was truncated at the
 * first `//` — the single most destructive defect in the old engine (recon §3.1). This rule
 * cannot express that: it iterates over tokens the scanner already classified, and the text
 * inside a `String` token is not a comment token.
 *
 * That last sentence is true and was not sufficient, because a `'…'` or `“…”` literal is not
 * a `String` token. `{'url': 'http://example.com/a', 'ok': 1}` scans as `Unknown("'http")`,
 * `Colon`, `LineComment("//example.com/a', 'ok': 1}")` — the rest of the user's document IS
 * a comment token to jsonc — and deleting it reproduced the legacy defect exactly, on the
 * single most common non-JSON paste this tool exists to accept. `{'note': 'block /* x *\/
 * text'}` was worse still: the result parsed, reported zero problems, and was missing the
 * user's words.
 *
 * So this is the one rule that works on the raw stream and therefore has to ask
 * {@link isInsideOpaqueSpan} the question every other rule gets for free from
 * `structuralTokens`: is this offset inside text the grammar must not read?
 */
export const removeCommentRule: RepairRule = {
  id: 'remove-comment',
  confidence: 'safe',
  priority: 10,
  resolves: ['jsonc/comment'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const proposals: RepairProposal[] = [];

    for (const token of ctx.tokens) {
      if (token.kind !== TokenKind.LineComment && token.kind !== TokenKind.BlockComment) continue;
      if (isInsideOpaqueSpan(ctx.source, ctx.tokens, token.from)) continue;
      proposals.push(deletion(token.from, token.to, kindOf(token)));
    }

    // `hashCommentSpans` is already computed in the same left-to-right pass as the foreign
    // literals, so a `#` inside one never opens a span in the first place.
    for (const span of hashCommentSpans(ctx.source, ctx.tokens)) {
      proposals.push(deletion(span.from, span.to, 'hash'));
    }

    return proposals;
  },
};

function kindOf(token: Token): string {
  return token.kind === TokenKind.LineComment ? 'line' : 'block';
}

function deletion(from: number, to: number, form: string): RepairProposal {
  return {
    ruleId: 'remove-comment',
    span: { from, to },
    edits: [{ from, to, insert: '' }],
    labelCode: 'fix.remove-comment',
    labelParams: { form },
    resolves: 'jsonc/comment',
  };
}
